import { type fabric } from 'fabric';

type UndoCompletionHandler = (event?: fabric.IEvent) => void;

type DefaultUndoCompletionEvent = 'moved' | 'scaled' | 'rotated' | 'skewed';

// Add to this list when completing undo on a new event. This represents the
// standard set of events that will complete an undo group for most objects.
type UndoCompletionEvent = DefaultUndoCompletionEvent | 'mouseup';

const DEFAULT_UNDO_COMPLETION_EVENTS: UndoCompletionEvent[] = [
  'moved',
  'scaled',
  'rotated',
  'skewed',
] as const;

interface UndoBehaviorsInterface {
  initializeUndoBehaviors(): void;
  setUndoCompletionEvents(eventNames: UndoCompletionEvent[]): void;
  resetUndoCompletionEvents(): void;
  getUndoCompletionEvents(): readonly UndoCompletionEvent[];
}

type UndoTarget = fabric.Object & UndoBehaviorsInterface;

const createHandler = (target: UndoTarget): UndoCompletionHandler => {
  return () => {
    target.controller?.undoManager?.completeUndoGroup();
  };
};

const clearUndoHandlers = (target: UndoTarget) => {
  const handlers = target.undoCompletionHandlers;
  if (!handlers) return;

  for (const [eventName, handler] of handlers.entries()) {
    target.off(eventName, handler);
  }

  handlers.clear();
};

const registerDefaultUndoEvents = (target: UndoTarget) => {
  target.setUndoCompletionEvents([...DEFAULT_UNDO_COMPLETION_EVENTS]);
};

const UndoBehaviors = {
  initializeUndoBehaviors(this: UndoTarget): void {
    if (this.undoBehaviorsInitialized) return;

    this.undoCompletionHandlers = new Map();
    this.undoBehaviorsInitialized = true;
    registerDefaultUndoEvents(this);
  },

  setUndoCompletionEvents(this: UndoTarget, eventNames: UndoCompletionEvent[]) {
    // This should never happen. In part these checks satisfy access to undoCompletionHandlers
    // below
    if (!this.undoBehaviorsInitialized || !this.undoCompletionHandlers) {
      console.error(
        'Undo behaviors not initialized on object',
        this.type,
        this.id
      );
      return;
    }

    clearUndoHandlers(this);

    for (const eventName of eventNames) {
      const handler = createHandler(this);
      this.undoCompletionHandlers.set(eventName, handler);
      this.on(eventName, handler);
    }
  },

  resetUndoCompletionEvents(
    this: fabric.Object & UndoBehaviorsInterface
  ): void {
    if (!this.undoBehaviorsInitialized || !this.undoCompletionHandlers) return;

    clearUndoHandlers(this);
    registerDefaultUndoEvents(this);
  },

  getUndoCompletionEvents(this: fabric.Object & UndoBehaviorsInterface) {
    if (!this.undoBehaviorsInitialized || !this.undoCompletionHandlers) {
      return [];
    }

    return Array.from(this.undoCompletionHandlers.keys());
  },
};

export default UndoBehaviors;
export type { UndoBehaviorsInterface, UndoCompletionEvent };
